import { AfterViewChecked, ChangeDetectorRef, Component, EventEmitter, forwardRef, Input, Output } from '@angular/core'
import { ControlValueAccessor, NG_VALUE_ACCESSOR, UntypedFormControl } from '@angular/forms'
import { ErrorStateMatcher } from '@angular/material/core'
import { TranslatePipe } from '@ngx-translate/core'

@Component({
    selector: 'app-form-input',
    templateUrl: 'form-input.component.html',
    styleUrls: ['./form-input.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => FormInputComponent),
            multi: true,
        },
        TranslatePipe,
    ],
    standalone: false,
})
export class FormInputComponent implements ControlValueAccessor, AfterViewChecked {
    @Input() title: string
    @Input() placeholder: string
    @Input() mask: string
    @Input() control: UntypedFormControl
    @Input() type: 'text' | 'password' | 'number' | 'email' | 'textarea' = 'text'
    @Input() public errorLookup: { [key: string]: string } = {}
    @Input() errorStateMatcher: ErrorStateMatcher
    @Input() hint: string
    @Input() hintAlign: 'start' | 'end' = 'start'
    @Input() rows: number = 3
    @Input() maxlength: number
    @Input() suffix: string
    @Input() allowDecimals = false
    @Input() maxInputDigits?: number
    @Input() inputId: string
    @Output() blurred = new EventEmitter()
    @Output() focused = new EventEmitter()
    @Output() keydown = new EventEmitter<KeyboardEvent>()

    _value = ''

    constructor(private cdRef: ChangeDetectorRef) {}

    public get required(): boolean {
        if (!this.control || !this.control.validator) {
            return false
        }
        // Create a temporary control with an empty value to test for required validator
        const validator = this.control.validator({} as UntypedFormControl)
        return validator && validator['required']
    }

    public get errors(): Array<string> {
        if (!this.control) return []
        if (!(this.control.touched || this.control.dirty)) return []
        const validationErrors = this.control.errors || {}
        return Object.keys(validationErrors).map((key) => this.errorLookup[key])
    }

    public get autocomplete(): string {
        return this.type === 'password' ? 'new-password' : 'off'
    }

    public get isDisabled(): boolean {
        return this.control?.disabled ?? false
    }

    propagateChange: any = () => {}

    public ngAfterViewChecked() {
        this.cdRef.detectChanges()
    }

    public writeValue(value: any) {
        if (value) {
            this._value = value
        }
    }

    public registerOnChange(fn) {
        this.propagateChange = fn
    }

    public registerOnTouched() {}

    public onChange(event) {
        this.propagateChange(event.target.value)
    }

    public onNumberInput(event: Event): void {
    const input = event.target as HTMLInputElement
    if (!this.allowDecimals) {
      this.onChange(event)
      return
    }

    const sanitized = input.value.replace(/[^\d.,]/g, '')
    const separatorIndex = sanitized.search(/[.,]/)
    const integerPart = separatorIndex === -1 ? sanitized : sanitized.slice(0, separatorIndex)
    const limitedIntegerPart =
      this.maxInputDigits === undefined ? integerPart : integerPart.slice(0, this.maxInputDigits)
    const decimalPart = separatorIndex === -1 ? '' : sanitized.slice(separatorIndex + 1).replace(/[.,]/g, '')
    const limitedDecimalPart = this.maxInputDigits === undefined ? decimalPart : decimalPart.slice(0, 2)
    input.value = separatorIndex === -1 ? limitedIntegerPart : `${limitedIntegerPart},${limitedDecimalPart}`
    this.control?.setValue(input.value)
    this.propagateChange(input.value)
  }
}
